import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Consultation, GoalEvaluation, ConsultationGoal } from "@/types/consultation";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  useCreateGoalEvaluationMutation,
  useUpdateGoalEvaluationMutation,
  useGetAllGoalEvaluationsQuery,
  useDeleteGoalEvaluationMutation,
} from "@/services/consultationApi";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Target, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { LoadingButton } from "@/components/ui/common/loading-button";

const evaluationSchema = z.object({
  goal_start_date: z.string().min(1, "Goal Start Date is required"),
  goal_evaluation: z.string().min(1, "Goal Evaluation is required"),
  tentative_goal_achievement_date: z.string().min(1, "Tentative Goal Achievement Date is required"),
  actual_goal_achievement_date: z.string().optional().nullable(),
  satisfied_with_performance: z.enum(["Yes", "No"], {
    required_error: "Please select if you are satisfied with your performance",
  }),
  satisfied_with_gym: z.enum(["Yes", "No"], {
    required_error: "Please select if you are satisfied with the gym",
  }),
  hardships_overcome: z.string().optional().nullable(),
});

type EvaluationFormValues = z.infer<typeof evaluationSchema>;

interface GoalEvaluationSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  data: Consultation;
  initialSelectedGoalId?: number;
}

const GoalEvaluationSheet = ({
  isOpen,
  onOpenChange,
  data,
  initialSelectedGoalId,
}: GoalEvaluationSheetProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  
  const { data: evaluationsData, refetch: refetchEvaluations } =
    useGetAllGoalEvaluationsQuery({
      consultation_id: data.id ?? 0,
      org_id: orgId,
    },
    { skip: !data.id || !isOpen }
  );

  const [createEvaluation, { isLoading: isCreating }] = useCreateGoalEvaluationMutation();
  const [updateEvaluation, { isLoading: isUpdating }] = useUpdateGoalEvaluationMutation();
  const [deleteEvaluation, { isLoading: isDeleting }] = useDeleteGoalEvaluationMutation();

  const form = useForm<EvaluationFormValues>({
    resolver: zodResolver(evaluationSchema),
    defaultValues: {
      goal_start_date: "",
      goal_evaluation: "",
      tentative_goal_achievement_date: "",
      actual_goal_achievement_date: null,
      satisfied_with_performance: undefined,
      satisfied_with_gym: undefined,
      hardships_overcome: "",
    },
  });

  const [selectedGoal, setSelectedGoal] = useState<ConsultationGoal | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const existingEvaluation = useMemo(() => {
    if (!selectedGoal || !evaluationsData?.evaluations) return null;
    return evaluationsData.evaluations.find((ev) => ev.goal_id === selectedGoal.id);
  }, [selectedGoal, evaluationsData]);

  useEffect(() => {
    if (isOpen && initialSelectedGoalId && data.goals) {
      const goal = data.goals.find(g => g.id === initialSelectedGoalId);
      if (goal) {
        setSelectedGoal(goal);
      }
    }
  }, [isOpen, initialSelectedGoalId, data.goals]);

  useEffect(() => {
    if (existingEvaluation) {
      form.reset({
        goal_start_date: existingEvaluation.start_date,
        goal_evaluation: existingEvaluation.evaluation_text,
        tentative_goal_achievement_date: existingEvaluation.goal_achievement,
        actual_goal_achievement_date: existingEvaluation.goal_achievement || null,
        satisfied_with_performance: existingEvaluation.is_satisfied ? "Yes" : "No",
        satisfied_with_gym: existingEvaluation.gym_satisfaction ? "Yes" : "No",
        hardships_overcome: existingEvaluation.description || "",
      });
    } else {
      form.reset({
        goal_start_date: "",
        goal_evaluation: "",
        tentative_goal_achievement_date: "",
        actual_goal_achievement_date: null,
        satisfied_with_performance: undefined,
        satisfied_with_gym: undefined,
        hardships_overcome: "",
      });
    }
  }, [existingEvaluation, form, selectedGoal]);

  const onSubmit = async (values: EvaluationFormValues) => {
    if (!selectedGoal || !data.id) return;

    const payload: GoalEvaluation = {
      goal_id: selectedGoal.id!,
      start_date: values.goal_start_date,
      evaluation_text: values.goal_evaluation,
      goal_achievement: values.tentative_goal_achievement_date,
      is_satisfied: values.satisfied_with_performance === "Yes",
      gym_satisfaction: values.satisfied_with_gym === "Yes",
      description: values.hardships_overcome || undefined,
    };

    try {
      if (existingEvaluation) {
        await updateEvaluation({
          consultation_id: data.id,
          org_id: orgId,
          data: { goal_evaluations: [{ ...payload, id: existingEvaluation.id }] },
        }).unwrap();
        toast({ description: "Evaluation updated successfully", variant: "success" });
      } else {
        await createEvaluation({
          consultation_id: data.id,
          org_id: orgId,
          data: { goal_evaluations: [payload] },
        }).unwrap();
        toast({ description: "Evaluation created successfully", variant: "success" });
      }
      setSelectedGoal(null);
      onOpenChange(false);
    } catch (error: unknown) {
      console.error("Failed to save evaluation", error);
      toast({ description: "Failed to save evaluation", variant: "destructive" });
    }
  };

  const handleDelete = async (goalId: number) => {
    if (!data.id) return;
    try {
      await deleteEvaluation({
        consultation_id: data.id,
        goal_id: goalId,
        org_id: orgId,
      }).unwrap();
      toast({ description: "Evaluation deleted successfully", variant: "success" });
      if (selectedGoal?.id === goalId) {
        setSelectedGoal(null);
      }
    } catch (error: unknown) {
      console.error("Failed to delete evaluation", error);
      toast({ description: "Failed to delete evaluation", variant: "destructive" });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[1000px] overflow-y-auto custom-scrollbar">
        <SheetHeader>
          <SheetTitle>Goal Evaluation</SheetTitle>
        </SheetHeader>
        <div className="py-6 space-y-6">
          {selectedGoal ? (
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-semibold text-primary">
                  Evaluating: {selectedGoal.goal_text}
                </h4>
              </div>

              <Form {...form}>
                <form 
                  onSubmit={(e) => {
                    e.stopPropagation();
                    form.handleSubmit(onSubmit)(e);
                  }} 
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Goal Start Date */}
                    <FormField
                      control={form.control}
                      name="goal_start_date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Goal Start Date *</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(new Date(field.value), "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Tentative Goal Achievement Date */}
                    <FormField
                      control={form.control}
                      name="tentative_goal_achievement_date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Tentative Goal Achievement Date *</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(new Date(field.value), "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Actual Goal Achievement Date */}
                    <FormField
                      control={form.control}
                      name="actual_goal_achievement_date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Actual Goal Achievement Date (Optional)</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(new Date(field.value), "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Performance Satisfaction */}
                    <FormField
                      control={form.control}
                      name="satisfied_with_performance"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Are you satisfied with your performance? *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select Yes / No" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Gym Satisfaction */}
                    <FormField
                      control={form.control}
                      name="satisfied_with_gym"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Are you satisfied with the gym in helping you achieve this goal? *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select Yes / No" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Goal Evaluation */}
                  <FormField
                    control={form.control}
                    name="goal_evaluation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Goal Evaluation *</FormLabel>
                        <FormControl>
                          <FloatingLabelInput
                            {...field}
                            type="textarea"
                            label="Briefly evaluate your progress towards this goal"
                            className="min-h-[100px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Hardships Overcome */}
                  <FormField
                    control={form.control}
                    name="hardships_overcome"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Briefly describe the hardships you faced and how you overcame them (Optional)</FormLabel>
                        <FormControl>
                          <FloatingLabelInput
                            {...field}
                            value={field.value || ""}
                            type="textarea"
                            label="Describe any challenges and your solutions"
                            className="min-h-[100px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-3 pt-4">
                    {existingEvaluation && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isDeleting}
                        className="mr-auto"
                      >
                        {isDeleting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="mr-2 h-4 w-4" />
                        )}
                        Delete Evaluation
                      </Button>
                    )}
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isCreating || isUpdating}
                      disabled={isCreating || isUpdating}
                      className="min-w-[120px]"
                    >
                      {existingEvaluation ? "Update Evaluation" : "Save Evaluation"}
                    </LoadingButton>
                  </div>
                </form>
              </Form>
            </div>
          ) : (
             <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
               <Target className="size-10 mb-2 opacity-20" />
               <p>No goal selected for evaluation.</p>
             </div>
          )}
        </div>
      </SheetContent>

      <AlertDialog 
        open={showDeleteConfirm} 
        onOpenChange={setShowDeleteConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the evaluation for this goal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (selectedGoal?.id) {
                  handleDelete(selectedGoal.id);
                  setShowDeleteConfirm(false);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
};

export default GoalEvaluationSheet;
